/**
 * Carrusel de opiniones: fetch API + clonado de <template> Liquid en vez de strings HTML.
 * El slideshow-component debe montarse ya con todas las slideshow-slide antes de conectar al DOM.
 */

const COMMENT_MAX_CHARS = 120;

/** URL por defecto del listado Trusted Shop (también bloque review). */
const NW_TRUSTEDSHOP_REVIEWS_LIST_URL = '/apps/api/reviews/trustedshop/list';


/**
 * @typedef {{
 *   submitted_at: string,
 *   customer_first_name?: string,
 *   comment?: string,
 *   rating: number,
 *   title?: string,
 *   type: 'PRODUCT_REVIEW' | 'SERVICE_REVIEW' | string,
 *   sku?: string,
 *   product_name?: string,
 *   product_url?: string,
 *   images?: Array<{url_original?: string, path_thumbnail?: string}>,
 *   reply?: {comment?: string, created_at?: string, updated_at?: string},
 * }} Review
 */

/**
 * Escapa HTML para inyección segura de texto de usuario.
 * @param {string | null | undefined} s
 * @returns {string}
 */
function esc(s) {
  if (s == null || s === '') return '';
  const d = document.createElement('div');
  d.textContent = String(s);
  return d.innerHTML;
}

/**
 * @param {any} json
 * @returns {Review[]}
 */
function parseTrustedShopReviewList(json) {
  if (Array.isArray(json)) return json;
  if (json && Array.isArray(json.data)) return json.data;
  if (json && Array.isArray(json.reviews)) return json.reviews;
  return [];
}

/**
 * Reseñas precargadas desde metafield custom.reviews_last (misma forma que la API).
 * @param {HTMLElement} root
 * @returns {Review[]}
 */
function readInitialReviewsFromMetafield(root) {
  const el = root.querySelector('script[data-nw-reviews-initial-json]');
  if (!el?.textContent?.trim()) return [];
  try {
    const parsed = parseTrustedShopReviewList(JSON.parse(el.textContent));
    return parsed.filter((r) => r.type === 'PRODUCT_REVIEW' || r.type === 'SERVICE_REVIEW');
  } catch {
    return [];
  }
}

/**
 * Si hay referencia y existen reseñas para esa referencia → solo esas. Si no hay ninguna (API devuelve últimas genéricas) → todas las de tipo reseña.
 * Sin referencia → lista genérica sin filtrar por producto.
 * @param {Review[]} raw
 * @param {string | undefined} requestedReference
 * @returns {{ reviews: Review[], generic: boolean }}
 */
function reviewsForCarouselRequest(raw, requestedReference) {
  const want = String(requestedReference ?? '').trim();
  const base = raw.filter((r) => r.type === 'PRODUCT_REVIEW' || r.type === 'SERVICE_REVIEW');
  if (!want) {
    return { reviews: base, generic: true };
  }
  const matched = base.filter((r) => r.sku != null && String(r.sku).trim() === want);
  if (matched.length > 0) {
    return { reviews: matched, generic: false };
  }
  return { reviews: base, generic: true };
}

/** @type {Map<string, Promise<{ reviews: Review[], generic: boolean }>>} */
const trustedShopReviewsForReferenceCache = new Map();

/**
 * @param {string} locale
 * @param {string} url
 * @param {string | undefined} [reference]
 */
function getTrustedShopReviewsForReference(locale, url, reference) {
  const referenceKey = String(reference ?? '').trim();
  const key = `${url}|${locale}|${referenceKey || '__null__'}`;
  let p = trustedShopReviewsForReferenceCache.get(key);
  if (p) return p;

  p = (async () => {
    // El endpoint sigue esperando la clave `sku`, pero el valor funcional ahora es custom.reference.
    const body = { locale, sku: referenceKey || null };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = /** @type {any} */ (await response.json());
    const raw = parseTrustedShopReviewList(json);
    return reviewsForCarouselRequest(raw, reference);
  })();

  trustedShopReviewsForReferenceCache.set(key, p);
  p.catch(() => trustedShopReviewsForReferenceCache.delete(key));
  return p;
}

/**
 * Genera los nodos SVG de 5 estrellas con la misma lógica de redondeo que blocks/review.liquid.
 * Las vacías usan fill: var(--star-half-empty-color) vía CSS; las llenas fill: var(--star-fill-color).
 * @param {number} rating
 * @returns {DocumentFragment}
 */
function buildStarsFragment(rating) {
  const r = Number(rating) || 0;
  const decimal = r % 1;
  let dr = 0;
  if (decimal >= 0.3 && decimal <= 0.7) dr = 0.5;
  else if (decimal > 0.7) dr = 1;
  const filled = Math.floor(r) + Math.ceil(dr);

  const frag = document.createDocumentFragment();
  for (let i = 1; i <= 5; i++) {
    const isF = i <= filled;
    const isH = dr === 0.5 && i === filled;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', isF ? 'stars filled-star' : 'stars');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.setAttribute('role', 'presentation');
    svg.setAttribute('aria-hidden', 'true');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#nw-reviews-star');
    if (isH) use.setAttribute('fill', 'url(#nw-reviews-half)');
    svg.appendChild(use);
    frag.appendChild(svg);
  }
  return frag;
}

/**
 * Actualiza la cabecera de confianza con el agregado del producto cuando está disponible.
 * Las tarjetas pueden limitarse a las opiniones recientes, por lo que no deben recalcular
 * el score ni el total global que ya aporta el metafield `custom.reviews_rating`.
 * @param {HTMLElement} root
 * @param {Review[]} reviews
 */
function updateTrustSummary(root, reviews) {
  const summaryEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-summary]'));
  const starsEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-avg-stars]'));
  const scoreEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-avg-score]'));
  const countEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-count]'));
  if (!summaryEl || !starsEl || !scoreEl || !countEl) return;

  const aggregateScoreRaw = root.dataset.aggregateScore?.trim() || '';
  const aggregateCountRaw = root.dataset.aggregateCount?.trim() || '';
  const aggregateScore = aggregateScoreRaw === '' ? Number.NaN : Number(aggregateScoreRaw);
  const aggregateCount = aggregateCountRaw === '' ? Number.NaN : Number(aggregateCountRaw);
  const ratings = reviews.map((review) => Number(review.rating)).filter(Number.isFinite);
  const score = Number.isFinite(aggregateScore) ? aggregateScore : ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
  const count = Number.isFinite(aggregateCount) && aggregateCount > 0 ? aggregateCount : ratings.length;

  if (!ratings.length || !Number.isFinite(score) || !count) {
    summaryEl.hidden = true;
    return;
  }

  starsEl.replaceChildren(buildStarsFragment(score));
  scoreEl.textContent = score.toFixed(1);
  countEl.textContent = `(${count})`;
  summaryEl.hidden = false;
}

/**
 * @param {string} dateString
 * @returns {string}
 */
function formatDate(dateString) {
  if (!dateString) return '';
  try {
    return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

/**
 * Si comentario y título son el mismo texto (tras trim), no mostrar el comentario (el título ya basta).
 * @param {string | null | undefined} title
 * @param {string | null | undefined} comment
 */
function reviewCommentEqualsTitle(title, comment) {
  const t = String(title ?? '').trim();
  const c = String(comment ?? '').trim();
  return t.length > 0 && c.length > 0 && t === c;
}

/**
 * Clona la <template data-nw-review-card-template> y rellena los data-field con los datos de la reseña.
 * @param {Review} review
 * @param {HTMLTemplateElement} cardTpl
 * @param {I18n} i18n
 * @param {{ showProductLink?: boolean }} [opts]
 * @returns {DocumentFragment}
 */
function buildCardFragment(review, cardTpl, i18n, opts = {}) {
  const showProductLink = opts.showProductLink === true;
  const frag = /** @type {DocumentFragment} */ (cardTpl.content.cloneNode(true));

  /** @param {string} field @returns {Element | null} */
  const f = (field) => frag.querySelector(`[data-field="${field}"]`);

  // Estrellas: data-field="rating-stars" es el propio div.rating (flex container + aria-label)
  const starsEl = f('rating-stars');
  if (starsEl) {
    starsEl.setAttribute('aria-label', i18n.stars.replace('RATING', String(review.rating)));
    starsEl.appendChild(buildStarsFragment(review.rating));
  }

  const ratingValueEl = f('rating-value');
  if (ratingValueEl) {
    const rating = Number(review.rating);
    ratingValueEl.textContent = Number.isFinite(rating) ? `${rating.toFixed(2)} / 5.00` : '';
    ratingValueEl.toggleAttribute('hidden', !Number.isFinite(rating));
  }

  // Meta: autor + fecha
  const metaEl = f('meta');
  if (metaEl) {
    const date = formatDate(review.submitted_at);
    const author = review.customer_first_name || '';
    metaEl.textContent = [author, date].filter(Boolean).join(' · ');
  }

  const productLinkEl = f('product-link');
  if (productLinkEl && showProductLink && review.product_name && review.product_url) {
    productLinkEl.textContent = '';
    const a = document.createElement('a');
    a.href = String(review.product_url);
    a.textContent = String(review.product_name);
    a.className = 'nw-reviews-carousel__product-link-anchor';
    productLinkEl.appendChild(a);
    productLinkEl.removeAttribute('hidden');
  }

  // Título
  const titleEl = f('title');
  if (titleEl && review.title) {
    titleEl.textContent = review.title;
    titleEl.removeAttribute('hidden');
  }

  // Comentario truncado (omitir si es igual al título: ya se muestra arriba en negrita)
  const commentEl = f('comment');
  if (commentEl && review.comment && !reviewCommentEqualsTitle(review.title, review.comment)) {
    const c = review.comment;
    commentEl.textContent = c.length > COMMENT_MAX_CHARS ? `${c.slice(0, COMMENT_MAX_CHARS).trimEnd()}…` : c;
    commentEl.removeAttribute('hidden');
  }

  // Imágenes
  const imagesEl = f('images');
  if (imagesEl && review.images?.length) {
    for (const img of review.images.slice(0, 4)) {
      const src = img.url_original || `https://attachments.etrusted.com${img.path_thumbnail}`;
      const imgEl = document.createElement('img');
      imgEl.className = 'nw-reviews-carousel__thumb';
      imgEl.src = esc(src);
      imgEl.alt = '';
      imgEl.loading = 'lazy';
      imgEl.width = 44;
      imgEl.height = 44;
      imagesEl.appendChild(imgEl);
    }
    imagesEl.removeAttribute('hidden');
  }

  // Respuesta del comercio
  const replyEl = f('reply');
  if (replyEl && review.reply?.comment) {
    replyEl.removeAttribute('hidden');
  }

  return frag;
}

/**
 * Rellena el <dialog data-nw-review-modal> con el contenido completo de una reseña y lo abre.
 * @param {Review} review
 * @param {HTMLDialogElement} dialog
 * @param {HTMLTemplateElement} modalTpl
 * @param {I18n} i18n
 * @param {{ showProductLink?: boolean }} [opts]
 */
function openReviewModal(review, dialog, modalTpl, i18n, opts = {}) {
  const showProductLink = opts.showProductLink === true;
  const body = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-nw-review-modal-body]'));
  if (!body) return;

  // Vaciar contenido anterior
  body.innerHTML = '';

  const frag = /** @type {DocumentFragment} */ (modalTpl.content.cloneNode(true));

  /** @param {string} field @returns {Element | null} */
  const f = (field) => frag.querySelector(`[data-field="${field}"]`);

  // Estrellas
  const starsEl = f('modal-stars');
  if (starsEl) {
    starsEl.setAttribute('aria-label', i18n.stars.replace('RATING', String(review.rating)));
    starsEl.appendChild(buildStarsFragment(review.rating));
  }

  // Meta: fecha + autor si existe
  const metaEl = f('modal-meta');
  if (metaEl) {
    const date = formatDate(review.submitted_at);
    const author = review.customer_first_name ? `${review.customer_first_name}.` : '';
    metaEl.textContent = [date, author].filter(Boolean).join(' · ');
  }

  const modalProductLink = /** @type {HTMLElement | null} */ (f('modal-product-link'));
  if (modalProductLink) {
    modalProductLink.textContent = '';
    modalProductLink.hidden = true;
    if (showProductLink && review.product_name && review.product_url) {
      const a = document.createElement('a');
      a.href = String(review.product_url);
      a.textContent = String(review.product_name);
      a.className = 'nw-review-modal__product-link-anchor';
      modalProductLink.appendChild(a);
      modalProductLink.removeAttribute('hidden');
    }
  }

  // Título
  const titleEl = f('modal-title');
  if (titleEl && review.title) {
    titleEl.textContent = review.title;
    titleEl.removeAttribute('hidden');
  }

  // Comentario completo (omitir si es igual al título)
  const commentEl = f('modal-comment');
  if (commentEl && review.comment && !reviewCommentEqualsTitle(review.title, review.comment)) {
    commentEl.textContent = review.comment;
    commentEl.removeAttribute('hidden');
  }

  // Todas las imágenes
  const imagesEl = f('modal-images');
  if (imagesEl && review.images?.length) {
    for (const img of review.images) {
      const src = img.url_original || `https://attachments.etrusted.com${img.path_thumbnail}`;
      const imgEl = document.createElement('img');
      imgEl.className = 'nw-review-modal__thumb';
      imgEl.src = esc(src);
      imgEl.alt = '';
      imgEl.loading = 'lazy';
      imgEl.width = 80;
      imgEl.height = 80;
      imagesEl.appendChild(imgEl);
    }
    imagesEl.removeAttribute('hidden');
  }

  // Respuesta del comercio
  const replyEl = f('modal-reply');
  const replyTextEl = f('modal-reply-text');
  const replyDateEl = f('modal-reply-date');
  if (replyEl && replyTextEl && review.reply?.comment) {
    replyTextEl.textContent = review.reply.comment;
    if (replyDateEl && review.reply.created_at) {
      replyDateEl.textContent = formatDate(review.reply.created_at);
    }
    replyEl.removeAttribute('hidden');
  }

  // Botón de cierre
  const closeBtn = frag.querySelector('[data-nw-review-modal-close]');
  if (closeBtn) {
    closeBtn.setAttribute('aria-label', i18n.close);
    closeBtn.addEventListener('click', () => dialog.close());
  }

  body.appendChild(frag);
  dialog.showModal();
}

/**
 * @typedef {{ prev: string, next: string, stars: string, open: string, verified: string, close: string, dialog: string, loadMore: string }} I18n
 */

/**
 * @param {Review} review
 */
function reviewDedupeKey(review) {
  return `${review.submitted_at || ''}|${review.sku || ''}`;
}

/**
 * @param {DocumentFragment} cardFrag
 * @param {Review} review
 * @param {HTMLDialogElement | null} dialog
 * @param {HTMLTemplateElement | null} modalTpl
 * @param {I18n} i18n
 * @param {boolean} genericList
 */
function wireReviewCardInteractions(cardFrag, review, dialog, modalTpl, i18n, genericList) {
  const hasModalContent = !!(
    review.comment ||
    review.title ||
    review.images?.length ||
    review.reply?.comment ||
    (genericList && review.product_name && review.product_url)
  );
  const cardWrapper = /** @type {HTMLElement | null} */ (cardFrag.querySelector('[data-nw-review-open]'));
  if (cardWrapper && dialog && modalTpl && hasModalContent) {
    cardWrapper.setAttribute('aria-label', i18n.open);
    const openModal = () => openReviewModal(review, dialog, modalTpl, i18n, { showProductLink: genericList });
    /** @param {Event} e */
    const isFromProductOrAnyLink = (e) => {
      const t = e.target;
      const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
      return Boolean(el?.closest('a[href]'));
    };
    cardWrapper.addEventListener('click', (e) => {
      if (isFromProductOrAnyLink(e)) return;
      openModal();
    });
    cardWrapper.addEventListener('keydown', (e) => {
      if (isFromProductOrAnyLink(e)) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openModal();
      }
    });
  } else if (cardWrapper) {
    cardWrapper.removeAttribute('role');
    cardWrapper.removeAttribute('tabindex');
    cardWrapper.removeAttribute('aria-label');
    cardWrapper.style.cursor = 'default';
  }
}

/**
 * @param {Review} review
 * @param {HTMLTemplateElement} cardTpl
 * @param {HTMLTemplateElement} slideTpl
 * @param {HTMLDialogElement | null} dialog
 * @param {HTMLTemplateElement | null} modalTpl
 * @param {I18n} i18n
 * @param {boolean} genericList
 * @returns {HTMLElement}
 */
function createReviewSlide(review, cardTpl, slideTpl, dialog, modalTpl, i18n, genericList) {
  const slideFrag = /** @type {DocumentFragment} */ (slideTpl.content.cloneNode(true));
  const slide = /** @type {HTMLElement} */ (slideFrag.firstElementChild);
  slide.dataset.reviewKey = reviewDedupeKey(review);

  const cardFrag = buildCardFragment(review, cardTpl, i18n, { showProductLink: genericList });
  wireReviewCardInteractions(cardFrag, review, dialog, modalTpl, i18n, genericList);
  slide.appendChild(cardFrag);
  return slide;
}

/**
 * @param {HTMLTemplateElement} slideTpl
 * @param {I18n} i18n
 * @param {() => void} onLoadMore
 * @returns {DocumentFragment}
 */
function createLoadMoreSlide(slideTpl, i18n, onLoadMore) {
  const slideFrag = /** @type {DocumentFragment} */ (slideTpl.content.cloneNode(true));
  const slide = /** @type {HTMLElement} */ (slideFrag.firstElementChild);
  slide.setAttribute('data-nw-reviews-load-more-slide', '');
  slide.classList.add('nw-reviews-carousel__load-more-slide');

  const wrap = document.createElement('div');
  wrap.className = 'nw-reviews-carousel__load-more';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'nw-reviews-carousel__load-more-btn';
  btn.textContent = i18n.loadMore;
  btn.addEventListener('click', onLoadMore);

  wrap.appendChild(btn);
  slide.appendChild(wrap);
  return slide;
}

/**
 * Añade reseñas de la API al carrusel existente (sin reemplazar las del metafield).
 * @param {HTMLElement} mount
 * @param {HTMLElement} root
 * @param {Review[]} displayedReviews
 * @param {Review[]} apiReviews
 * @param {boolean} genericList
 * @param {HTMLDialogElement | null} dialog
 * @param {HTMLTemplateElement | null} modalTpl
 * @param {I18n} i18n
 * @returns {Review[]}
 */
function appendReviewsFromApi(mount, root, displayedReviews, apiReviews, genericList, dialog, modalTpl, i18n) {
  const cardTpl = /** @type {HTMLTemplateElement | null} */ (root.querySelector('[data-nw-review-card-template]'));
  const slideTpl = /** @type {HTMLTemplateElement | null} */ (root.querySelector('[data-nw-review-slide-template]'));
  if (!cardTpl || !slideTpl) return [];

  const existingKeys = new Set(displayedReviews.map(reviewDedupeKey));
  const toAdd = apiReviews.filter((r) => !existingKeys.has(reviewDedupeKey(r)));

  mount.querySelector('[data-nw-reviews-load-more-slide]')?.remove();

  toAdd.forEach((review) => {
    mount.appendChild(createReviewSlide(review, cardTpl, slideTpl, dialog, modalTpl, i18n, genericList));
  });

  root.dispatchEvent(new CustomEvent('nw:reviews-carousel:ready', { bubbles: true, detail: { root } }));
  return toAdd;
}

/**
 * Construye las slides de Swiper clonando los <template> del DOM.
 * @param {Review[]} reviews
 * @param {HTMLElement} root
 * @param {HTMLDialogElement | null} dialog
 * @param {HTMLTemplateElement | null} modalTpl
 * @param {I18n} i18n
 * @param {boolean} [genericList] Listado genérico (últimas de la tienda): enlace a producto en cada tarjeta.
 * @param {{ onLoadMore?: () => void }} [opts]
 * @returns {HTMLElement}
 */
function buildCarouselFromTemplates(reviews, root, dialog, modalTpl, i18n, genericList = false, opts = {}) {
  const cardTpl = /** @type {HTMLTemplateElement | null} */ (root.querySelector('[data-nw-review-card-template]'));
  const slideTpl = /** @type {HTMLTemplateElement | null} */ (root.querySelector('[data-nw-review-slide-template]'));

  if (!cardTpl || !slideTpl) {
    throw new Error('nw-reviews-carousel: faltan <template> en el DOM');
  }

  const showLoadMore = typeof opts.onLoadMore === 'function';
  const slides = document.createDocumentFragment();

  const dlg = dialog;
  const tpl = modalTpl;
  reviews.forEach((review) => {
    slides.appendChild(createReviewSlide(review, cardTpl, slideTpl, dlg, tpl, i18n, genericList));
  });

  if (showLoadMore && opts.onLoadMore) {
    slides.appendChild(createLoadMoreSlide(slideTpl, i18n, opts.onLoadMore));
  }

  return slides;
}

/**
 * @param {HTMLElement} root
 */
async function initNwReviewsCarousel(root) {
  const reference = root.dataset.reference;
  const locale = root.dataset.locale || 'es_ES';
  const reviewsListUrl = root.dataset.proxyReviewsUrl || NW_TRUSTEDSHOP_REVIEWS_LIST_URL;
  /** @type {I18n} */
  const i18n = {
    prev: /** @type {string} */ (root.dataset.i18nPrev),
    next: /** @type {string} */ (root.dataset.i18nNext),
    stars: /** @type {string} */ (root.dataset.i18nStars),
    open: /** @type {string} */ (root.dataset.i18nOpen),
    verified: /** @type {string} */ (root.dataset.i18nVerified),
    close: /** @type {string} */ (root.dataset.i18nClose),
    dialog: /** @type {string} */ (root.dataset.i18nDialog),
    loadMore: /** @type {string} */ (root.dataset.i18nLoadMore),
  };

  const loading = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-loading]'));
  const errEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-error]'));
  const emptyEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-empty]'));
  const mount = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-mount]'));
  const swiper = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-reviews-swiper]'));
  const dialog = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-nw-review-modal]'));
  const modalTpl = /** @type {HTMLTemplateElement | null} */ (root.querySelector('[data-nw-review-modal-template]'));

  if (!reference || !mount || !swiper) return;

  // Cerrar modal al clicar el backdrop (fuera del body del modal)
  if (dialog) {
    dialog.setAttribute('aria-label', i18n.dialog);
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    // Cerrar con Escape ya lo gestiona el navegador de forma nativa con <dialog>
  }

  /** @type {Review[]} */
  let displayedReviews = [];

  const dlg = dialog && modalTpl ? dialog : null;
  const tpl = dialog && modalTpl ? modalTpl : null;

  /**
   * @param {Review[]} reviews
   * @param {boolean} generic
   * @param {boolean} withLoadMore
   */
  const renderCarousel = (reviews, generic, withLoadMore) => {
    if (emptyEl) emptyEl.hidden = true;
    if (errEl) errEl.hidden = true;
    const loadMoreOpts = withLoadMore ? { onLoadMore: () => loadFromApi(true) } : {};
    mount.replaceChildren(buildCarouselFromTemplates(reviews, root, dlg, tpl, i18n, generic, loadMoreOpts));
    displayedReviews = reviews;
    updateTrustSummary(root, reviews);
    swiper.hidden = false;
    root.dispatchEvent(new CustomEvent('nw:reviews-carousel:ready', { bubbles: true, detail: { root } }));
  };

  /** @param {boolean} [fromLoadMore] */
  const loadFromApi = async (fromLoadMore = false) => {
    const loadMoreBtn = /** @type {HTMLButtonElement | null} */ (
      mount.querySelector('.nw-reviews-carousel__load-more-btn')
    );

    if (fromLoadMore && loadMoreBtn) {
      loadMoreBtn.disabled = true;
      loadMoreBtn.setAttribute('aria-busy', 'true');
    } else if (loading) {
      loading.hidden = false;
    }
    if (errEl) errEl.hidden = true;

    try {
      const { reviews, generic } = await getTrustedShopReviewsForReference(locale, reviewsListUrl, reference);

      if (fromLoadMore) {
        if (loadMoreBtn) {
          loadMoreBtn.disabled = false;
          loadMoreBtn.removeAttribute('aria-busy');
        }
        if (!reviews.length) return;

        const added = appendReviewsFromApi(mount, root, displayedReviews, reviews, generic, dlg, tpl, i18n);
        if (added.length) displayedReviews = [...displayedReviews, ...added];
        return;
      }

      if (loading) loading.hidden = true;

      if (!reviews.length) {
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      renderCarousel(reviews, generic, false);
    } catch {
      if (loadMoreBtn) {
        loadMoreBtn.disabled = false;
        loadMoreBtn.removeAttribute('aria-busy');
      }
      if (loading) loading.hidden = true;
      if (fromLoadMore) return;
      if (errEl) errEl.hidden = false;
    }
  };

  const initialRaw = readInitialReviewsFromMetafield(root);
  if (initialRaw.length > 0) {
    const { reviews, generic } = reviewsForCarouselRequest(initialRaw, reference);
    if (reviews.length > 0) {
      if (loading) loading.hidden = true;
      renderCarousel(reviews, generic, true);
      return;
    }
  }

  await loadFromApi(false);
}

// /**
//  * Mismo criterio que `block_settings.stars_style` en blocks/review.liquid (sin data-* en el DOM).
//  * @param {HTMLElement | null} row `.rating`
//  * @returns {'single' | 'outline' | 'shaded'}
//  */
// function inferProductBlockStarsStyle(row) {
//   const slots = row ? [...row.querySelectorAll(':scope > svg.stars')] : [];
//   if (slots.length <= 1) return 'single';
//   const first = /** @type {SVGElement | undefined} */ (slots[0]);
//   if (first && first.querySelectorAll('use').length >= 2) return 'outline';
//   return 'shaded';
// }
//
// /**
//  * Rellena el bloque de estrellas del producto (`data-nw-review-inject` en blocks/review.liquid).
//  * @param {HTMLElement} root
//  */
// async function initNwReviewBlockFromApi(root) {
//   const reference = root.dataset.reference;
//   if (!reference) return;
//
//   const locale = root.dataset.locale || 'es_ES';
//   const showNumber = root.dataset.showNumber !== 'false';
//   const i18nStars = root.dataset.i18nStars || '';
//
//   try {
//     const { reviews, generic } = await getTrustedShopReviewsForReference(locale, NW_TRUSTEDSHOP_REVIEWS_LIST_URL, reference);
//     if (!reviews.length || generic) return;
//
//     const sum = reviews.reduce((/** @type {number} */ s, /** @type {Review} */ r) => s + Number(r.rating), 0);
//     const avg = Math.round((sum / reviews.length) * 10) / 10;
//
//     const row = /** @type {HTMLElement | null} */ (root.querySelector('.rating'));
//     const starsStyle = inferProductBlockStarsStyle(row);
//     const slots = row ? /** @type {SVGElement[]} */ ([...row.querySelectorAll(':scope > svg.stars')]) : [];
//
//     if (row && slots.length) {
//       const decimal = avg % 1;
//       let dr = 0;
//       if (decimal >= 0.3 && decimal <= 0.7) dr = 0.5;
//       else if (decimal > 0.7) dr = 1;
//       const filled = Math.floor(avg) + Math.ceil(dr);
//
//       slots.forEach((svg, idx) => {
//         const starIdx = idx + 1;
//         svg.classList.toggle('filled-star', starIdx <= filled);
//         const usePrimary = svg.querySelector('use');
//         if (usePrimary && starsStyle !== 'single') {
//           if (dr === 0.5 && starIdx === filled) {
//             usePrimary.setAttribute('fill', 'url(#half)');
//           } else {
//             usePrimary.removeAttribute('fill');
//           }
//         }
//       });
//
//       if (starsStyle === 'single' && slots[0]) {
//         slots[0].classList.toggle('filled-star', avg > 0);
//       }
//     }
//
//     row?.setAttribute('aria-label', i18nStars.replace('RATING', avg.toFixed(1)));
//
//     const avgEl = root.querySelector('[data-nw-review-avg]');
//     if (avgEl) avgEl.textContent = avg.toFixed(1);
//
//     const avgMulti = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-review-avg-multi]'));
//     const sepMulti = /** @type {HTMLElement | null} */ (root.querySelector('[data-nw-review-sep-multi]'));
//     if (starsStyle !== 'single' && showNumber) {
//       if (avgMulti) avgMulti.textContent = avg.toFixed(1);
//       if (sepMulti) sepMulti.hidden = false;
//     }
//
//     const countEl = root.querySelector('[data-nw-review-count]');
//     if (countEl && showNumber) countEl.textContent = String(reviews.length);
//
//     root.hidden = false;
//   } catch {
//     // Sin reseñas para este SKU o error: el wrapper sigue oculto.
//   }
// }

function boot() {
  document.querySelectorAll('[data-nw-reviews-carousel]').forEach((el) => {
    if (el instanceof HTMLElement) initNwReviewsCarousel(el);
  });
  // document.querySelectorAll('[data-nw-review-inject]').forEach((el) => {
  //   if (el instanceof HTMLElement) initNwReviewBlockFromApi(el);
  // });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
